import apiClient from '../utils/axios';

export interface BillingImpactPreview {
  is_free_trial?: boolean;
  has_subscription: boolean;
  current_facility_count: number;
  new_facility_count: number;
  change: number;
  subscription_status?: string;
  billing_cycle?: string;
  immediate_charges?: boolean;
  proration_amount_cents?: number;
  proration_amount_dollars?: number;
  setup_fee_cents?: number;
  setup_fee_dollars?: number;
  total_immediate_charge_cents?: number;
  total_immediate_charge_dollars?: number;
  deferred_setup_fee_dollars?: number;
  currency?: string;
  is_estimated?: boolean;
  period_start?: number;
  period_end?: number;
  message?: string;
  pricing_info?: {
    monthly?: {
      unit_amount_dollars: number;
      price_id: string;
      currency: string;
      recurring_interval?: string;
    };
    annual?: {
      unit_amount_dollars: number;
      price_id: string;
      currency: string;
      recurring_interval?: string;
    };
    setup_fee?: {
      unit_amount_dollars: number;
      price_id: string;
      currency: string;
    };
  };
}

export interface SubscriptionUpdateResult {
  success: boolean;
  message: string;
  is_free_trial?: boolean;
  has_subscription?: boolean;
  subscription_id?: string;
  old_quantity?: number;
  new_quantity?: number;
  change?: number;
}

export class FacilityBillingService {
  
  static async previewBillingImpact(
    organizationId: string,
    newFacilityCount: number,
    token: string
  ): Promise<BillingImpactPreview> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/facility-billing-impact`,
      {
        params: { facility_count: newFacilityCount },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async updateFacilitySubscription(
    organizationId: string,
    newFacilityCount: number,
    token: string
  ): Promise<SubscriptionUpdateResult> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/update-facility-subscription`,
      {},
      {
        params: { facility_count: newFacilityCount },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async syncTrialInfoFromStripe(
    organizationId: string,
    token: string
  ): Promise<any> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/sync-trial-info`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async getTrialStatusFromStripe(
    organizationId: string,
    token: string
  ): Promise<any> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/trial-status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async syncAllFromStripe(
    organizationId: string,
    token: string
  ): Promise<any> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/stripe/sync-all`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async debugStripeStatus(
    organizationId: string,
    token: string
  ): Promise<any> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/debug-status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static formatCurrency(amountInCents: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountInCents / 100);
  }

  static formatCurrencyFromDollars(amountInDollars: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountInDollars);
  }

  static getBillingImpactMessage(preview: BillingImpactPreview): string {
    if (preview.is_free_trial) {
      if (preview.change > 0) {
        return `Adding ${preview.change} facilit${preview.change > 1 ? 'ies' : 'y'} during your free trial. Setup fees and billing will start when your trial expires.`;
      } else if (preview.change < 0) {
        return `Removing ${Math.abs(preview.change)} facilit${Math.abs(preview.change) > 1 ? 'ies' : 'y'} from your organization.`;
      }
      return 'No changes to facility count.';
    }

    if (!preview.has_subscription) {
      if (preview.change > 0) {
        return `Adding ${preview.change} facilit${preview.change > 1 ? 'ies' : 'y'}. Setup fees and subscription will be charged when you start billing.`;
      } else if (preview.change < 0) {
        return `Removing ${Math.abs(preview.change)} facilit${Math.abs(preview.change) > 1 ? 'ies' : 'y'} from your organization.`;
      }
      return 'No changes to facility count.';
    }

    // Has subscription - check if charges are immediate or deferred
    const isTrialing = preview.subscription_status === 'trialing';
    const hasImmediateCharges = preview.immediate_charges && preview.total_immediate_charge_dollars && preview.total_immediate_charge_dollars > 0;
    const hasDeferredCharges = !preview.immediate_charges && preview.deferred_setup_fee_dollars && preview.deferred_setup_fee_dollars > 0;

    if (preview.change > 0) {
      if (hasImmediateCharges) {
        const prorationAmount = this.formatCurrencyFromDollars(preview.proration_amount_dollars || 0);
        const setupFee = this.formatCurrencyFromDollars(preview.setup_fee_dollars || 0);
        const totalCharge = this.formatCurrencyFromDollars(preview.total_immediate_charge_dollars || 0);
        
        return `Adding ${preview.change} facilit${preview.change > 1 ? 'ies' : 'y'} will immediately charge:\n• Prorated subscription: ${prorationAmount}\n• Setup fee: ${setupFee}\n• Total charge: ${totalCharge}`;
      } else if (hasDeferredCharges) {
        const deferredFee = this.formatCurrencyFromDollars(preview.deferred_setup_fee_dollars || 0);
        return `Adding ${preview.change} facilit${preview.change > 1 ? 'ies' : 'y'} during trial. Setup fee of ${deferredFee} will be charged when your trial ends.`;
      } else if (isTrialing) {
        return `Adding ${preview.change} facilit${preview.change > 1 ? 'ies' : 'y'} during trial. Setup fees and subscription billing will start when your trial ends.`;
      }
    } else if (preview.change < 0) {
      if (preview.immediate_charges) {
        const creditAmount = this.formatCurrencyFromDollars(Math.abs(preview.proration_amount_dollars || 0));
        return `Removing ${Math.abs(preview.change)} facilit${Math.abs(preview.change) > 1 ? 'ies' : 'y'} will reduce future billing. You may receive a credit of approximately ${creditAmount}.`;
      } else {
        return `Removing ${Math.abs(preview.change)} facilit${Math.abs(preview.change) > 1 ? 'ies' : 'y'} will reduce billing when your trial ends.`;
      }
    }

    return 'No changes to facility count.';
  }

  static getActionButtonText(preview: BillingImpactPreview, actionType: 'add' | 'remove' | 'change'): string {
    if (preview.is_free_trial || !preview.has_subscription) {
      if (actionType === 'add') return 'Add Facility';
      if (actionType === 'remove') return 'Remove Facility';
      return 'Update Facility';
    }

    const hasImmediateCharges = preview.immediate_charges && preview.total_immediate_charge_dollars && preview.total_immediate_charge_dollars > 0;

    if (preview.change > 0) {
      if (hasImmediateCharges) {
        const totalCharge = this.formatCurrencyFromDollars(preview.total_immediate_charge_dollars || 0);
        return `Charge ${totalCharge} & Add Facilit${preview.change > 1 ? 'ies' : 'y'}`;
      } else {
        return `Add Facilit${preview.change > 1 ? 'ies' : 'y'} (Trial)`;
      }
    } else if (preview.change < 0) {
      return `Remove Facilit${Math.abs(preview.change) > 1 ? 'ies' : 'y'}`;
    }

    return 'Update Facility';
  }

  static getBillingImpactType(preview: BillingImpactPreview): 'success' | 'warning' | 'info' | 'error' {
    if (preview.is_free_trial || !preview.has_subscription) {
      return 'info';
    }

    const hasImmediateCharges = preview.immediate_charges && preview.total_immediate_charge_dollars && preview.total_immediate_charge_dollars > 0;

    if (preview.change > 0 && hasImmediateCharges) {
      return 'warning';
    }

    if (preview.change > 0 && !preview.immediate_charges) {
      return 'info'; // Trial - charges deferred
    }

    if (preview.change < 0) {
      return 'success';
    }

    return 'info';
  }
} 